import {
  BarChart2,
  FilePlus2,
  FileText,
  FileX2,
  Globe,
  Library,
  Sparkles,
  UserSquare2 as SquareUserRound,
  Workflow,
  type LucideIcon,
} from 'lucide-react'

import { Text } from '@/components/ui/Text'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  getThreadPlaceholders,
  resolveThreadLanguage,
  ThreadPlaceholderTemplates,
} from '@/lib/constants/languages'
import { TwinsMarkLoader } from '@/components/quick-study-results/TwinsMarkLoader'

/**
 * What the thread shows between sending a question and the first token.
 *
 * Retrieval runs before generation, so this gap is routinely several seconds on
 * a large library, long enough that an empty bubble reads as a hang.
 *
 * WHAT IT SAYS IS DRIVEN BY THE STREAM, not by a clock. The turn already
 * announces every leg it runs (`tool_start`) and every leg that finishes
 * (`tool_end`), and `ThreadDetailPage` collects those into the `legs` prop.
 * `tool_start.query` is the model's own lookup string when it sent one.
 *
 * Phase copy lives in `THREAD_PLACEHOLDERS`. A new tool needs a target and a
 * chip noun in every language there. Chip glyphs are a separate listing:
 * `ACTIVITY_CHIP_ICONS`. A tool with copy but no glyph still renders, because
 * the fallback icon is honest about "a source" rather than crashing. A tool
 * with no fragment yet still renders the generic working line.
 *
 * The visible row is ChatGPT-shaped: a short verb plus citation-style chips
 * (icon + noun). The long joined sentence is the accessible name only, so two
 * web legs cannot print "the web, the web" and a 56-character lookup cannot
 * stretch the line. The query still lives on the chip tooltip.
 *
 * The mark is the platform's own waiting glyph (TwinsMarkLoader), the same one
 * Quick Study shows while it thinks: it already carries the motion, so nothing
 * else in this row needs to animate.
 *
 * The copy follows the THREAD's language, not the browser's. This line sits in
 * the same column as the answer that replaces it, so an English wait ahead of a
 * Portuguese answer was the seam worth closing
 * (docs/plans/home-lab-composer-language-and-recent-study.md, D3 and D4).
 */

/**
 * One retrieval leg as the waiting line sees it: what it is, and whether it has
 * reported back.
 *
 * `tool` is the wire's `AssistantToolName` but is typed as a plain string on
 * purpose. This component must render a leg whose name it has never heard of
 * rather than fail to compile against a frozen union, which is the whole point
 * of deriving the phase from counts instead of from names.
 */
export interface ThreadActivityLeg {
  tool: string
  /** Flipped by that leg's `tool_end`, whether it succeeded or failed. */
  done: boolean
  /** Model lookup string from `tool_start.query`, when the call carried one. */
  query?: string
}

export interface ThreadActivityChip {
  tool: string
  label: string
  query: string | null
}

interface ThreadActivityIndicatorProps {
  /** The thread's answer language. Null or absent falls back to English. */
  language?: string | null
  /**
   * Legs reported so far, in `tool_start` order. Absent or empty is a real
   * state, not missing data: it is every turn before its first frame lands, and
   * every turn that plans no retrieval at all.
   */
  legs?: Array<ThreadActivityLeg>
}

/** Long enough to name the topic; short enough to stay on one quiet line. */
const ACTIVITY_QUERY_MAX = 56

const CHIP_CLASS_NAME =
  'inline-flex max-w-[12rem] items-center gap-1.5 rounded-full border-0 bg-muted px-2.5 py-1 text-left'

/**
 * Joins target fragments the way the thread's own language joins a list.
 *
 * `Intl.ListFormat` rather than a per-language conjunction in the copy table:
 * the conjunction, the separators and whether a list takes one at all are facts
 * about the language, and the platform ships one already. The guard is for a
 * runtime without it, where a comma-joined list is worse prose but still true.
 */
function formatTargets(targets: Array<string>, language: string): string {
  if (targets.length < 2) {
    return targets[0] ?? ''
  }

  try {
    return new Intl.ListFormat(language, {
      style: 'long',
      type: 'conjunction',
    }).format(targets)
  } catch {
    return targets.join(', ')
  }
}

export function clipActivityQuery(query: string | undefined): string | null {
  if (!query) {
    return null
  }

  const collapsed = query.trim().replace(/\s+/g, ' ')
  if (!collapsed) {
    return null
  }

  if (collapsed.length <= ACTIVITY_QUERY_MAX) {
    return collapsed
  }

  return `${collapsed.slice(0, ACTIVITY_QUERY_MAX - 1)}\u2026`
}

/**
 * Running named legs, one per tool. A turn can announce `web_search` twice;
 * the sentence and the chips both name the source once.
 */
function uniqueNamedRunning(params: {
  legs: Array<ThreadActivityLeg>
  copy: ThreadPlaceholderTemplates
}): Array<ThreadActivityLeg> {
  const { legs, copy } = params
  const seen = new Set<string>()
  const unique: Array<ThreadActivityLeg> = []

  for (const leg of legs) {
    if (leg.done || !copy.targets[leg.tool] || seen.has(leg.tool)) {
      continue
    }

    seen.add(leg.tool)
    unique.push(leg)
  }

  return unique
}

/**
 * The line to show for a set of legs. Pure, and exported so the phase rules can
 * be tested without a render.
 *
 * Three phases, in the order a turn passes through them:
 *
 *   no legs yet          -> `working`, which claims nothing it cannot back
 *   at least one open    -> `searching` / `searchingFor`, naming only the
 *                           legs STILL RUNNING (and one query when present)
 *   every leg reported   -> `composing`, because retrieval is genuinely over
 *
 * A leg that ended is dropped from the sentence whether it succeeded or failed.
 * The waiting line's job is to say what the reader is waiting ON, and a finished
 * leg is not it. What a failed leg produced belongs in the answer, which already
 * reports its own gaps.
 */
export function threadActivityLabel(params: {
  legs: Array<ThreadActivityLeg>
  copy: ThreadPlaceholderTemplates
  /** Already resolved through `resolveThreadLanguage`. */
  language: string
}): string {
  const { legs, copy, language } = params

  if (legs.length === 0) {
    return copy.working
  }

  const running = legs.filter((leg) => !leg.done)
  if (running.length === 0) {
    return copy.composing
  }

  const named = uniqueNamedRunning({ legs, copy })
  const targets = named
    .map((leg) => copy.targets[leg.tool])
    .filter((target): target is string => Boolean(target))

  if (targets.length === 0) {
    return copy.working
  }

  const query = clipActivityQuery(
    named.find((leg) => Boolean(leg.query?.trim()))?.query,
  )
  const filled = (query ? copy.searchingFor : copy.searching).replace(
    '{targets}',
    formatTargets(targets, language),
  )

  return query ? filled.replace('{query}', query) : filled
}

/**
 * Citation-style chips for the visible row. Empty when the turn is not in the
 * searching phase (no named running tool), so the component can fall back to
 * the plain sentence.
 */
export function threadActivityChips(params: {
  legs: Array<ThreadActivityLeg>
  copy: ThreadPlaceholderTemplates
}): Array<ThreadActivityChip> {
  const { legs, copy } = params

  if (legs.length === 0 || legs.every((leg) => leg.done)) {
    return []
  }

  return uniqueNamedRunning({ legs, copy })
    .map((leg) => {
      const label = copy.chips[leg.tool]
      if (!label) {
        return null
      }

      return {
        tool: leg.tool,
        label,
        query: clipActivityQuery(leg.query),
      }
    })
    .filter((chip): chip is ThreadActivityChip => chip != null)
}

/**
 * Glyph per named chip. `read_audience` is Lucide `square-user-round`; this
 * package ships it as `UserSquare2` (lucide-react 0.292). Keep the alias so
 * the listing matches lucide.dev.
 */
export const ACTIVITY_CHIP_ICONS: Record<string, LucideIcon> = {
  web_search: Globe,
  document_search: Library,
  read_skill: Sparkles,
  read_workflow: Workflow,
  read_audience: SquareUserRound,
  read_results: BarChart2,
  analyze_audience: BarChart2,
  read_artifact: FileText,
  update_artifact: FileText,
  create_artifact: FilePlus2,
  delete_artifact: FileX2,
  run_workflow: Workflow,
}

function ActivityChipIcon(props: { tool: string }) {
  const { tool } = props
  const Icon = ACTIVITY_CHIP_ICONS[tool] ?? Globe

  return (
    <Icon
      className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
      aria-hidden
      data-testid={`thread-activity-source-icon-${tool}`}
    />
  )
}

interface ThreadActivitySourceChipProps {
  chip: ThreadActivityChip
}

function ThreadActivitySourceChip(props: ThreadActivitySourceChipProps) {
  const { chip } = props

  const inner = (
    <>
      <ActivityChipIcon tool={chip.tool} />
      <Text as="span" size="xs" className="truncate">
        {chip.label}
      </Text>
    </>
  )

  if (!chip.query) {
    return (
      <span
        className={CHIP_CLASS_NAME}
        data-testid={`thread-activity-source-chip-${chip.tool}`}
      >
        {inner}
      </span>
    )
  }

  return (
    <Tooltip>
      <TooltipTrigger
        className={CHIP_CLASS_NAME}
        data-testid={`thread-activity-source-chip-${chip.tool}`}
      >
        {inner}
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="max-w-xs"
        data-testid={`thread-activity-source-chip-query-${chip.tool}`}
      >
        <Text size="xs">{chip.query}</Text>
      </TooltipContent>
    </Tooltip>
  )
}

export function ThreadActivityIndicator(props: ThreadActivityIndicatorProps) {
  const { language, legs } = props

  const resolved = resolveThreadLanguage(language)
  const copy = getThreadPlaceholders(resolved)
  const resolvedLegs = legs ?? []
  const label = threadActivityLabel({
    legs: resolvedLegs,
    copy,
    language: resolved,
  })
  const chips = threadActivityChips({
    legs: resolvedLegs,
    copy,
  })

  const searchingRow =
    chips.length > 0 ? (
      <span
        className="flex min-w-0 flex-wrap items-center gap-1.5"
        aria-hidden
        data-testid="thread-activity-search-row"
      >
        <Text size="sm" color="muted">
          {copy.searchingLead}
        </Text>
        {chips.map((chip) => (
          <ThreadActivitySourceChip key={chip.tool} chip={chip} />
        ))}
      </span>
    ) : (
      <Text size="sm" color="muted">
        {label}
      </Text>
    )

  return (
    <div
      className="flex items-center gap-2.5"
      data-testid="thread-activity-indicator"
      role="status"
      aria-live="polite"
      aria-label={label}
    >
      <TwinsMarkLoader
        className="h-4 w-4 text-muted-foreground"
        dataTestId="thread-activity-mark"
      />
      <TooltipProvider delayDuration={150}>{searchingRow}</TooltipProvider>
    </div>
  )
}
